import React from 'react';
import { Link } from 'react-router-dom';
import {
  UsersIcon,
  AcademicCapIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const Dashboard: React.FC = () => {
  // 임시 통계 데이터
  const stats = {
    totalCourses: 24,
    activeCourses: 18,
    totalStudents: 342,
    todayApplications: 12,
    monthlyRevenue: 4850000,
    completionRate: 87
  };

  const recentApplications = [
    { id: 1, student: '김민수', course: '비즈니스 영어회화', time: '10분 전', status: 'pending' },
    { id: 2, student: '이지은', course: '토익 집중반', time: '25분 전', status: 'approved' },
    { id: 3, student: '박준호', course: '어린이 영어교실', time: '1시간 전', status: 'pending' },
    { id: 4, student: '최서연', course: '여행 영어', time: '2시간 전', status: 'approved' },
    { id: 5, student: '정다윤', course: '면접 영어 특강', time: '3시간 전', status: 'rejected' }
  ];

  const upcomingCourses = [
    { id: 1, name: '비즈니스 영어회화', startDate: '2024-12-15', applicants: 18, capacity: 20 },
    { id: 2, name: '토익 집중반', startDate: '2024-12-20', applicants: 25, capacity: 25 },
    { id: 3, name: '어린이 영어교실', startDate: '2025-01-05', applicants: 12, capacity: 15 },
    { id: 4, name: '여행 영어', startDate: '2025-01-10', applicants: 8, capacity: 20 }
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">대시보드</h1>
        <p className="text-gray-600">전체 운영 현황을 한눈에 확인하세요</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <AcademicCapIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">전체 강좌</p>
              <p className="text-2xl font-semibold text-gray-800">{stats.totalCourses}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <CalendarIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">진행중</p>
              <p className="text-2xl font-semibold text-gray-800">{stats.activeCourses}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <UsersIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">총 수강생</p>
              <p className="text-2xl font-semibold text-gray-800">{stats.totalStudents}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <ClockIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">오늘 신청</p>
              <p className="text-2xl font-semibold text-gray-800">{stats.todayApplications}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-red-100 rounded-lg">
              <CurrencyDollarIcon className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">월 수익</p>
              <p className="text-2xl font-semibold text-gray-800">
                {(stats.monthlyRevenue / 1000000).toFixed(1)}M
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-indigo-100 rounded-lg">
              <ChartBarIcon className="h-6 w-6 text-indigo-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">수료율</p>
              <p className="text-2xl font-semibold text-gray-800">{stats.completionRate}%</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Applications */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">최근 수강 신청</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {recentApplications.map((application) => (
                <div key={application.id} className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{application.student}</p>
                    <p className="text-sm text-gray-500">{application.course}</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="text-xs text-gray-500">{application.time}</span>
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        application.status === 'approved'
                          ? 'bg-green-100 text-green-800'
                          : application.status === 'rejected'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {application.status === 'approved'
                        ? '승인'
                        : application.status === 'rejected'
                        ? '거절'
                        : '대기'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <Link
                to="/admin/students"
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                전체 신청 목록 보기 →
              </Link>
            </div>
          </div>
        </div>

        {/* Upcoming Courses */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">개강 예정 강좌</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {upcomingCourses.map((course) => (
                <div key={course.id} className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{course.name}</p>
                    <p className="text-sm text-gray-500">시작일: {course.startDate}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {course.applicants}/{course.capacity}명
                    </p>
                    <div className="mt-1 w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          course.applicants >= course.capacity
                            ? 'bg-red-600'
                            : course.applicants >= course.capacity * 0.8
                            ? 'bg-yellow-600'
                            : 'bg-green-600'
                        }`}
                        style={{
                          width: `${Math.min(
                            (course.applicants / course.capacity) * 100,
                            100
                          )}%`
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <Link
                to="/admin/courses"
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                전체 강좌 관리 →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;